import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuthStore, useStudioStore } from '@/store'
import { useTelegram } from '@/hooks/useTelegram'
import { supabase } from '@/lib/supabase'
import { withAuthRecovery } from '@/lib/authRecovery'
import { mapRawStudiosToStore } from '@/lib/studioMapper'
import { decideInviteEntry } from '@/lib/inviteEntryPolicy'
import { resolveInvite } from '@/lib/inviteResolver'
import {
  createSupabaseRequestHeaders,
  getSupabaseCredentials,
} from '@/lib/supabaseCredentials'
import {
  clearPendingInvite,
  readPendingInvite,
  savePendingInvite
} from '@/lib/invitePersistence'
import { isMockInitData } from '@/lib/telegramInitData'
import { cn } from '@/lib/utils'
import { Card } from '@/components/ui'
import { Button } from '@/components/ui/Button'
import { LoadingScreen } from '@/components/LoadingScreen'
import { UserPlus, Heart, XCircle, CalendarDays, ShieldCheck, Building2 } from 'lucide-react'

function getPersonDisplayName(person?: {
  first_name?: string | null
  last_name?: string | null
  email?: string | null
  tg_username?: string | null
} | null, fallback = 'Без имени') {
  const fullName = [person?.first_name, person?.last_name].filter(Boolean).join(' ').trim()
  return fullName || person?.email || person?.tg_username || fallback
}

function formatInviteDate(value?: string | null) {
  if (!value) return 'Без ограничения'
  return new Date(value).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })
}

export function InvitePage() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { user, isAuthenticated, login } = useAuthStore()
  const { setStudios } = useStudioStore()
  const { hapticFeedback, isInTelegram } = useTelegram()
  
  const [loading, setLoading] = useState(true)
  const [accepting, setAccepting] = useState(false)
  const [inviteData, setInviteData] = useState<any>(null)
  const [error, setError] = useState<string | null>(null)
  const [accepted, setAccepted] = useState(false)
  const [inviteType, setInviteType] = useState<'studio' | 'management'>('studio')
  const [managementInviteData, setManagementInviteData] = useState<any>(null)
  const [autoAccept, setAutoAccept] = useState(false)
  const [inviteSessionAuthenticated, setInviteSessionAuthenticated] = useState(false)
  const [errorAction, setErrorAction] = useState<'retry' | 'login' | 'open_account' | 'open_site' | 'none'>('retry')

  // Name collection
  const [firstName, setFirstName] = useState(user?.first_name || '')
  const [lastName, setLastName] = useState(user?.last_name || '')
  const [position, setPosition] = useState('')
  const authRedirectPath = isInTelegram ? '/' : (token ? `/login?invite_token=${token}` : '/login')
  const isOwnerInvite = inviteData?.role === 'owner'
  const isSeniorTeacherInvite = inviteData?.role === 'senior_teacher'
  const hasVerifiedInviteSession = isAuthenticated && inviteSessionAuthenticated
  const hasManagementTargetIdentity = inviteType === 'management'
    && Boolean(managementInviteData?.target_first_name?.trim())
    && Boolean(managementInviteData?.target_last_name?.trim())
    && Boolean(managementInviteData?.target_position?.trim())
  const hasStaffTargetIdentity = inviteType === 'studio'
    && !isOwnerInvite
    && Boolean(inviteData?.target_first_name?.trim())
    && (isSeniorTeacherInvite || Boolean(inviteData?.target_last_name?.trim()))
  const hasInviteTargetIdentity = hasManagementTargetIdentity || hasStaffTargetIdentity
  // Legacy-командный инвайт мог вернуть пользователя после уже подтверждённого входа
  // без имени в профиле. Auto-accept в таком случае не должен требовать второе нажатие:
  // имя владелец при необходимости заполнит в карточке сотрудника.
  const shouldCollectName = hasVerifiedInviteSession
    && !isOwnerInvite
    && !hasInviteTargetIdentity
    && !(autoAccept && inviteType === 'studio')
  const shouldCollectPosition = hasVerifiedInviteSession
    && inviteType === 'management'
    && !isOwnerInvite
    && !hasManagementTargetIdentity
  const isInviteActionDisabled = accepting
    || (shouldCollectName && (!firstName.trim() || !lastName.trim()))
    || (shouldCollectPosition && !position.trim())

  const clearInvitePersistence = () => {
    clearPendingInvite()
  }

  console.log('🎯 InvitePage - RENDERING with token:', token, 'isAuthenticated:', isAuthenticated, 'user:', user?.id)
  console.log('🎯 InvitePage - current URL:', window.location.href)
  console.log('🎯 InvitePage - current pathname:', window.location.pathname)

  useEffect(() => {
    // Само открытие ссылки не является согласием принять приглашение. Pending-токен
    // сохраняется только в обработчике явного действия пользователя ниже.
    const pendingBeforeMount = readPendingInvite({ requireAutoAccept: true })

    // Если пользователь вернулся после логина (токен был сохранён ранее, AuthProvider его
    // подхватил и перенаправил сюда) — включаем auto-accept
    if (isAuthenticated && pendingBeforeMount?.token === token) {
      console.log('🎯 InvitePage: auto-accept mode enabled (user returned after login)')
      setAutoAccept(true)
    }

    console.log('🎯 InvitePage useEffect: Starting invite check, token:', token)

    if (token) {
      checkInviteResolution()
    } else {
      setError('Токен приглашения не найден в URL')
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  useEffect(() => {
    const targetIdentity = inviteType === 'management' ? managementInviteData : inviteData
    if (!targetIdentity) return

    setFirstName((current) => current.trim() || targetIdentity.target_first_name || '')
    setLastName((current) => current.trim() || targetIdentity.target_last_name || '')
    if (inviteType === 'management') {
      setPosition((current) => current.trim() || targetIdentity.target_position || '')
    }
  }, [inviteType, managementInviteData, inviteData])

  // Auto-accept: когда данные инвайта загружены и autoAccept включен — сразу принимаем
  useEffect(() => {
    if (!autoAccept || !hasVerifiedInviteSession || accepting || accepted || isInviteActionDisabled) return
    if (inviteType === 'management' && managementInviteData) {
      console.log('🤖 InvitePage: auto-accepting management invite')
      acceptManagementInvite()
    } else if (inviteType === 'studio' && inviteData) {
      console.log('🤖 InvitePage: auto-accepting studio invite')
      acceptInvite()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoAccept, inviteData, managementInviteData, hasVerifiedInviteSession, isInviteActionDisabled])

  const refreshManagementAccess = async (): Promise<boolean> => {
    if (!user?.id) {
      return false
    }

    const maxAttempts = 5
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      const { data, error } = await withAuthRecovery(() =>
        (supabase as any).rpc('get_my_studios')
      ) as {
        data: any[] | null
        error: any
      }

      if (!error && data && data.length > 0) {
        const processedStudios = mapRawStudiosToStore(data)
        setStudios(processedStudios as any)

        const hasManagementAccess = processedStudios.some((studio: any) =>
          studio.access_kind === 'management_company' || studio.role === 'management_company'
        )
        console.log('🔄 refreshManagementAccess: Attempt', attempt, 'hasManagementAccess:', hasManagementAccess)

        if (hasManagementAccess) {
          return true
        }
      } else {
        console.warn('⚠️ refreshManagementAccess: Attempt failed', attempt, error)
      }

      if (attempt < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 400))
      }
    }

    return false
  }

  const refreshStudioAccess = async (): Promise<any[]> => {
    const maxAttempts = 5

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      const regular = await withAuthRecovery(() => (supabase as any).rpc('get_my_studios')) as any
      const error = regular.error
      const data = regular.data || []

      if (!error && data.length > 0) {
        const processedStudios = mapRawStudiosToStore(data)
        setStudios(processedStudios as any)
        console.log('🔄 refreshStudioAccess: studios visible on attempt', attempt, processedStudios.length)
        return data
      }

      console.warn('⚠️ refreshStudioAccess: attempt failed or returned no studios', attempt, error)

      if (attempt < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 400))
      }
    }

    return []
  }

  const getTerminalMessage = (reason: string) => {
    if (reason === 'access_revoked') {
      return 'Вы перешли по ссылке-приглашению. Доступ, ранее выданный по этой ссылке, был отозван. Запросите новое приглашение. Если у вас есть другие действующие доступы, войдите через my.levita.one.'
    }
    if (reason === 'identity_conflict') {
      return 'Вы перешли по ссылке-приглашению. Это приглашение нельзя применить к текущему аккаунту. Войдите в свой кабинет через my.levita.one или запросите приглашение для правильного аккаунта.'
    }

    let lead = 'Ссылка-приглашение недействительна.'
    if (reason === 'expired') lead = 'Вы перешли по ссылке-приглашению. Срок действия этой ссылки истёк.'
    if (reason === 'exhausted') lead = 'Вы перешли по ссылке-приглашению. Эта ссылка уже была использована или достигла лимита использований.'
    if (reason === 'revoked') lead = 'Вы перешли по ссылке-приглашению. Эта ссылка была отозвана.'

    return `${lead} Это не означает, что ранее выданный доступ отключён. Если вы уже принимали приглашение, войдите обычным способом через my.levita.one. Если доступа нет, запросите новую ссылку.`
  }

  const applyRegularPreview = (preview: any) => {
    if (!preview?.success || !preview.invite) {
      throw new Error(preview?.error || 'Вы перешли по ссылке-приглашению. Но его данные не удалось загрузить. Попробуйте снова')
    }

    const invite = {
      ...preview.invite,
      owner: preview.owner ?? null,
      invite_person: preview.invite_person ?? null,
      studios: preview.studios ?? [],
      cities: preview.cities ?? []
    }

    invite.studios = invite.studios.map((studio: any) => ({
      ...studio,
      studio_name: studio.studio_number,
      studio_address: studio.address,
      cities: invite.cities.find((city: any) => city.city_id === studio.city_id) || null
    }))
    setInviteData(invite)
  }

  const checkInviteResolution = async () => {
    if (!token) {
      setError('Токен приглашения не найден')
      setErrorAction('none')
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      setErrorAction('retry')

      const resolution = await resolveInvite(token)
      setInviteSessionAuthenticated(resolution.authenticated)
      const nextInviteType = resolution.invite_kind === 'management' ? 'management' : 'studio'
      setInviteType(nextInviteType)

      const decision = decideInviteEntry({
        kind: resolution.invite_kind,
        authenticated: resolution.authenticated,
        availability: resolution.invite_status,
        accountAccess: resolution.account_access,
        identityConflict: resolution.identity_conflict
      })

      if (decision.action === 'open_account') {
        clearInvitePersistence()
        navigate('/', { replace: true })
        return
      }

      if (decision.action === 'show_access_disabled') {
        clearInvitePersistence()
        setError('Доступ к приложению отключён администратором')
        setErrorAction('none')
        return
      }

      if (decision.action === 'show_terminal') {
        clearInvitePersistence()
        setError(getTerminalMessage(decision.reason))
        setErrorAction('open_site')
        return
      }

      if (nextInviteType === 'management') {
        if (!resolution.preview) throw new Error('Не удалось загрузить приглашение УК')
        setManagementInviteData(resolution.preview)
      } else {
        applyRegularPreview(resolution.preview)
      }
    } catch (err: any) {
      console.error('❌ Error resolving invite:', err)
      setError(err.message || 'Ошибка проверки приглашения')
      setErrorAction('retry')
    } finally {
      setLoading(false)
    }
  }

  const saveNameIfProvided = async () => {
    const trimFirst = firstName.trim()
      || managementInviteData?.target_first_name?.trim()
      || inviteData?.target_first_name?.trim()
      || ''
    const trimLast = lastName.trim()
      || managementInviteData?.target_last_name?.trim()
      || inviteData?.target_last_name?.trim()
      || ''
    if (!trimFirst && !trimLast) return
    if (trimFirst === (user?.first_name || '') && trimLast === (user?.last_name || '')) return
    try {
      await (supabase as any).rpc('update_user_profile', {
        p_first_name: trimFirst,
        p_last_name: trimLast
      })
      if (user) {
        login({
          ...user,
          first_name: trimFirst || undefined,
          last_name: trimLast || undefined,
          updated_at: new Date().toISOString()
        })
      }
    } catch (err) {
      console.warn('⚠️ saveNameIfProvided: failed to save name:', err)
    }
  }

  const acceptManagementInvite = async () => {
    console.log('🎯 acceptManagementInvite: Starting MC invite acceptance')
    
    if (!token) {
      setError('Нет токена приглашения')
      return
    }

    // Если пользователь не аутентифицирован, начинаем аутентификацию
    if (!hasVerifiedInviteSession || !user) {
      console.log('🔐 acceptManagementInvite: User not authenticated, starting auth')
      savePendingInvite(token, { autoAccept: true })
      navigate(authRedirectPath)
      return
    }

    if (shouldCollectName && (!firstName.trim() || !lastName.trim())) {
      setError('Укажите имя и фамилию')
      return
    }

    if (shouldCollectPosition && !position.trim()) {
      setError('Укажите должность')
      return
    }

    try {
      setAccepting(true)
      hapticFeedback.impact('light')
      
      console.log('🎯 acceptManagementInvite: Calling use-management-invite function')
      console.log('   - invite_code:', token)
      console.log('   - user_id:', user.id)
      
      // Используем Edge функцию
      const { url: supabaseUrl, apiKey } = getSupabaseCredentials()
      if (!supabaseUrl || !apiKey) {
        throw new Error('Не настроено подключение к Supabase')
      }
      
      const authToken = localStorage.getItem('auth_token')
      if (!authToken) {
        throw new Error('Нет токена авторизации')
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/use-management-invite`, {
        method: 'POST',
        headers: createSupabaseRequestHeaders(apiKey, authToken, {
          'Content-Type': 'application/json',
        }),
        body: JSON.stringify({
          invite_code: token,
          position: managementInviteData?.target_position?.trim() || position.trim() || null
        })
      })

      console.log('📊 acceptManagementInvite: Response status:', response.status)

      if (!response.ok) {
        const errorText = await response.text()
        console.error('📊 acceptManagementInvite: Error response:', errorText)
        throw new Error(`HTTP ${response.status}: ${errorText}`)
      }

      const result = await response.json()
      console.log('✅ acceptManagementInvite: Success result:', result)
      
      if (!result.success) {
        throw new Error(result.message || 'Ошибка активации инвайта')
      }

      const hasManagementAccess = await refreshManagementAccess()
      if (!hasManagementAccess) {
        console.warn('⚠️ acceptManagementInvite: management access not visible yet, redirecting anyway')
      }

      await saveNameIfProvided()

      setAccepted(true)
      hapticFeedback.notification('success')
      clearInvitePersistence()

      // Перенаправляем на страницу управляющей компании
      setTimeout(() => {
        console.log('🔄 Redirecting to management company dashboard')
        navigate('/management-dashboard', { state: { fromInvite: true } })
      }, 2000)
      
    } catch (err: any) {
      console.error('❌ Error accepting management invite:', err)
      setError(err.message || 'Ошибка принятия приглашения')
      hapticFeedback.notification('error')
    } finally {
      setAccepting(false)
    }
  }

  const getPostInviteRedirect = (result: any): string => {
    const studios = result.studios || []
    if (studios.length === 0) return '/partners'

    const mapped = mapRawStudiosToStore(studios)
    const isOrdinaryTeacher = result.role === 'teacher'
      || (mapped.length > 0 && mapped.every((studio: any) => studio.role === 'teacher' || studio.team_role === 'teacher'))
    if (isOrdinaryTeacher) return '/teachers'
    const partnerNumbers = [...new Set(mapped.map((s: any) => s.partner_number).filter((pn: any) => pn !== null && pn !== undefined))]
    const isOwnerAccess = result.role === 'owner' || mapped.some((s: any) => s.role === 'owner' || s.access_kind === 'owner')
    const isSeniorAccess = result.role === 'senior_teacher' || (mapped.length > 0 && mapped.every((s: any) => s.access_kind === 'senior_teacher'))

    if (partnerNumbers.length === 1) {
      const pn = partnerNumbers[0]
      if (isOwnerAccess) {
        return `/partners/${pn}/analytics`
      }

      const partnerStudios = mapped.filter((s: any) => s.partner_number === pn)
      if (partnerStudios.length > 0) {
        if (isSeniorAccess) return `/partners/${pn}/studios/${partnerStudios[0].studio_internal_id}/teachers`
        return `/partners/${pn}/studios/${partnerStudios[0].studio_internal_id}/metrics`
      }
    }

    return '/partners'
  }

  const applyInviteAuthResult = (result: any) => {
    if (result.token) {
      console.log('🔄 acceptInvite: Updating auth token')
      localStorage.setItem('auth_token', result.token)
    }

    const processedStudios = Array.isArray(result.studios)
      ? mapRawStudiosToStore(result.studios)
      : null

    // Access state must be committed before authentication. This prevents a render
    // with the previous account's studios (or an unverified empty store).
    if (processedStudios) {
      console.log('🔄 acceptInvite: Updating studios in store')
      setStudios(processedStudios as any)
      console.log('✅ acceptInvite: Studios processed and updated:', processedStudios.length)
    }

    if (result.user) {
      console.log('🔄 acceptInvite: Updating user data')
      const updatedUser = {
        ...result.user,
        studios: result.studios || []
      }
      login(updatedUser)
      console.log('✅ acceptInvite: User data updated')
    }
  }

  const acceptInvite = async () => {
    console.log('🎯 acceptInvite: Starting invite acceptance process')
    console.log('🎯 acceptInvite: Provided token:', token)
    console.log('🎯 acceptInvite: Current authentication status:', isAuthenticated)
    console.log('🎯 acceptInvite: Current user:', user?.id)
    
    if (!token) {
      console.log('❌ acceptInvite: No token available')
      setError('Нет токена приглашения')
      return
    }

    // Если пользователь не аутентифицирован, начинаем аутентификацию
    if (!hasVerifiedInviteSession || !user) {
      console.log('🔐 acceptInvite: User not authenticated, starting authentication process')
      console.log('🔐 acceptInvite: isAuthenticated:', isAuthenticated)
      console.log('🔐 acceptInvite: user exists:', !!user)
      
      savePendingInvite(token, { autoAccept: true })
      console.log('💾 acceptInvite: Saved pending invite and auto-accept marker')
      navigate(authRedirectPath)
      return
    }

    if (shouldCollectName && (!firstName.trim() || !lastName.trim())) {
      setError('Укажите имя и фамилию')
      return
    }

    try {
      setAccepting(true)
      hapticFeedback.impact('light')
      console.log('🎯 acceptInvite: User is authenticated, proceeding with invite redemption')
      console.log('🎯 acceptInvite: Redemption parameters:')
      console.log('   - invite_token:', token)
      console.log('   - target_user_id:', user.id)

      console.log('🔍 acceptInvite: Calling consume-invite Edge function')
      
      let result;
      const { url: supabaseUrl, apiKey } = getSupabaseCredentials()
      if (!supabaseUrl || !apiKey) {
        throw new Error('Не настроено подключение к Supabase')
      }
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      const appVersion = import.meta.env.VITE_APP_VERSION || null
      const telegramWebAppVersion = window.Telegram?.WebApp?.version || null
      const telegramPlatform = window.Telegram?.WebApp?.platform || null
      
      // Не отправляем timezone в dev режиме с mock данными
      const telegramData = window.Telegram?.WebApp?.initData || ''
      const isMockData = isMockInitData(telegramData)
      const finalTimezone = isMockData ? undefined : userTimezone
      
      console.log('📊 acceptInvite: Calling consume-invite Edge Function')
      console.log('📊 acceptInvite: supabaseUrl:', supabaseUrl)
      console.log('📊 acceptInvite: token:', token)
      console.log('📊 acceptInvite: userTimezone:', finalTimezone)
      console.log('📊 acceptInvite: isMockData:', isMockData)
      console.log('📊 acceptInvite: Edge function parameters:')
      console.log('   - invite_token:', token)
      console.log('   - inviteData full:', inviteData)
      console.log('   - initData length:', telegramData.length || 0)
      
      const authToken = localStorage.getItem('auth_token')
      if (telegramData && !hasStaffTargetIdentity) {
        const response = await fetch(`${supabaseUrl}/functions/v1/consume-invite`, {
          method: 'POST',
          headers: createSupabaseRequestHeaders(apiKey, apiKey, {
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify({
            initData: telegramData,
            timezone: finalTimezone,
            appVersion,
            telegramWebAppVersion,
            telegramPlatform,
            invite_token: token  // Используем токен, а не ID
          })
        })

        console.log('📊 acceptInvite: consume-invite function completed')
        console.log('📊 acceptInvite: response status:', response.status)

        if (!response.ok) {
          const errorText = await response.text()
          console.error('📊 acceptInvite: response error text:', errorText)
          throw new Error(`HTTP ${response.status}: ${errorText}`)
        }

        result = await response.json()
        console.log('✅ acceptInvite: Edge function succeeded')
        console.log('📊 acceptInvite: result:', result)
      } else if (authToken) {
        console.log('⚠️ acceptInvite: initData is empty, using JWT Path B')

        const response = await fetch(`${supabaseUrl}/functions/v1/consume-invite`, {
          method: 'POST',
          headers: createSupabaseRequestHeaders(apiKey, authToken, {
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify({
            invite_token: token
          })
        })

        if (!response.ok) {
          const errorText = await response.text()
          console.error('📊 acceptInvite: JWT path error text:', errorText)
          throw new Error(`HTTP ${response.status}: ${errorText}`)
        }

        result = await response.json()
        console.log('✅ acceptInvite: JWT path succeeded')
      } else {
        if (!authToken) {
          throw new Error('Нет авторизации')
        }
      }

      if (result.error) {
        console.error('❌ acceptInvite: Edge function returned error')
        console.error('❌ acceptInvite: Result details:', result)
        throw new Error(result?.error || 'Не удалось принять приглашение')
      }

      console.log('✅ acceptInvite: Invite accepted successfully')
      console.log('📊 acceptInvite: Success result details:', result)
      console.log('📊 acceptInvite: Team owner:', result.team_owner)
      console.log('📊 acceptInvite: Studios count:', result.studios?.length || 0)

      if (result.token) {
        console.log('🔄 acceptInvite: Storing fresh auth token before access refresh')
        localStorage.setItem('auth_token', result.token)
      }

      if (!result.studios?.length) {
        console.log('🔄 acceptInvite: result has no studios, refreshing access from get_my_studios')
        const refreshedStudios = await refreshStudioAccess()
        if (refreshedStudios.length > 0) {
          result = {
            ...result,
            studios: refreshedStudios
          }
        }
      }

      // Проверяем, есть ли у пользователя уже доступ
      const redirectPath = getPostInviteRedirect(result)
      console.log('🔄 acceptInvite: post-invite redirect path:', redirectPath)

      if (result.already_has_access || result.invite_not_used) {
        console.log('🔍 acceptInvite: User already has access or is owner')

        // Устанавливаем состояние "уже имеет доступ"
        setAccepted(true)

        applyInviteAuthResult(result)

        hapticFeedback.notification('success')
        clearInvitePersistence()

        setTimeout(() => {
          navigate(redirectPath, { state: { fromInvite: true } })
        }, 2000)
        return
      }

      // Обычный случай - новое приглашение принято

      applyInviteAuthResult(result)
      await saveNameIfProvided()

      setAccepted(true)
      hapticFeedback.notification('success')
      clearInvitePersistence()

      console.log('✅ acceptInvite: Studios updated, preparing redirect')

      // Ждем время, чтобы состояние успело обновиться во всех компонентах
      setTimeout(() => {
        console.log('🔄 Redirecting to:', redirectPath)
        navigate(redirectPath, { state: { fromInvite: true } })
      }, 3000)
      
    } catch (err: any) {
      console.error('❌ Error accepting invite:', err)
      setError(err.message || 'Ошибка принятия приглашения')
      hapticFeedback.notification('error')
    } finally {
      setAccepting(false)
    }
  }

  const declineInvite = () => {
    console.log('❌ User declined invite')
    console.log('🔄 declineInvite: Clearing any pending invite tokens to avoid re-processing')
    
    // Очищаем любые сохраненные токены приглашений
    clearInvitePersistence()
    
    console.log('🔄 declineInvite: Redirecting to main page without invite token')
    // Перенаправляем на главную страницу БЕЗ токена - обычная логика приложения
    navigate('/', { replace: true })
  }

  const handleErrorOk = () => {
    console.log('🔐 handleErrorOk: User acknowledged error, starting authentication process')
    console.log('🔐 handleErrorOk: Current error:', error)
    console.log('🔐 handleErrorOk: Current token:', token)
    
    // Сохраняем токен для возврата после аутентификации
    if (token) {
      console.log('💾 handleErrorOk: Saving pending invite token')
      savePendingInvite(token, { autoAccept: true })
      console.log('💾 handleErrorOk: Saved token:', token)
    } else {
      console.log('⚠️ handleErrorOk: No token to save')
    }
    
    console.log('🔄 handleErrorOk: Redirecting to main page for authentication')
    navigate(authRedirectPath)
  }

  const handleErrorAction = () => {
    if (errorAction === 'retry') {
      void checkInviteResolution()
      return
    }
    if (errorAction === 'login') {
      handleErrorOk()
      return
    }
    if (errorAction === 'open_account') {
      clearInvitePersistence()
      navigate('/', { replace: true })
      return
    }
    if (errorAction === 'open_site') {
      clearInvitePersistence()
      window.location.assign('https://my.levita.one/')
    }
  }

  if (loading) {
    return <LoadingScreen />
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="text-center">
          <XCircle className="w-12 h-12 text-error mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">Ошибка приглашения</h3>
          <p className="text-muted-foreground mb-4">{error}</p>
          <div className="space-y-3">
            {errorAction !== 'none' && (
              <Button onClick={handleErrorAction} className="w-full">
                {errorAction === 'login'
                  ? 'Войти в аккаунт'
                  : errorAction === 'open_site'
                    ? 'Перейти на главную'
                  : errorAction === 'open_account'
                    ? 'Перейти на главную'
                    : 'Попробовать снова'}
              </Button>
            )}
            <Button 
              onClick={declineInvite} 
              variant="outline" 
              className="w-full"
            >
              {errorAction === 'none' ? 'Перейти на главную' : 'Закрыть приглашение'}
            </Button>
          </div>
        </Card>
      </div>
    )
  }

  if (accepted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="text-center">
          <Heart className="w-12 h-12 text-primary mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">Приглашение принято!</h3>
          <p className="text-muted-foreground mb-4">
            Вы успешно получили доступ. Перенаправление...
          </p>
        </Card>
      </div>
    )
  }

  const accessLevelLabels = {
    standard: 'Стандартный доступ',
    advanced: 'Расширенный доступ'
  }

  const roleLabels = {
    owner: 'Владелец',
    manager: 'Управляющий',
    member: 'Сотрудник',
    senior_teacher: 'Старший педагог'
  }

  const inviteTargetName = isOwnerInvite
    ? getPersonDisplayName(inviteData?.invite_person, 'Новый владелец')
    : getPersonDisplayName(inviteData?.owner, 'Команда студии')
  const cityNames = (inviteData?.cities || []).map((city: any) => city.city_name).filter(Boolean)
  const studioNames = (inviteData?.studios || [])
    .map((studio: any) => studio.studio_name || studio.studio_number || studio.address)
    .filter(Boolean)
  const studioAccessSummary = isSeniorTeacherInvite
    ? 'Раздел «Педагоги» во всех городах франчайзи'
    : cityNames.length > 0
    ? `Студии в городах: ${cityNames.join(', ')}`
    : studioNames.length > 0
      ? studioNames.length <= 3
        ? `Доступ к студиям: ${studioNames.join(', ')}`
        : `Доступ к ${studioNames.length} студиям`
      : 'Доступ к студиям владельца'
  const inviteTitle = inviteType === 'management'
    ? 'Приглашение в управляющую компанию'
    : isOwnerInvite
      ? 'Приглашение для нового владельца'
      : isSeniorTeacherInvite
        ? 'Доступ для старшего педагога'
        : 'Приглашение в команду студии'
  const inviteSubtitle = inviteType === 'management'
    ? ''
    : isOwnerInvite
      ? ''
      : ''
  const invitePrimaryLine = inviteType === 'management'
    ? 'Управляющая компания LEVITA'
    : isOwnerInvite
      ? inviteTargetName
      : roleLabels[inviteData?.role as keyof typeof roleLabels] || inviteData?.role || 'Участник команды'
  const inviteSecondaryLine = inviteType === 'management'
    ? accessLevelLabels[managementInviteData?.access_level as keyof typeof accessLevelLabels] || managementInviteData?.access_level
    : isOwnerInvite
      ? 'Будет добавлен как владелец'
      : studioAccessSummary
  const validityDate = formatInviteDate(
    inviteType === 'management' ? managementInviteData?.valid_until : inviteData?.expires_at
  )

  // Рендеринг для Management Company инвайтов
  if (inviteType === 'management') {
    if (managementInviteData) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <Card className="w-full max-w-lg overflow-hidden border border-border/70 p-0 shadow-sm">
            <div className="border-b border-border/70 px-6 py-6 sm:px-7">
              <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <UserPlus className="h-6 w-6" />
              </div>
              <p className="mb-2 text-sm text-muted-foreground">{inviteTitle}</p>
              <h2 className="text-2xl font-semibold tracking-tight text-foreground">{invitePrimaryLine}</h2>
              {inviteSubtitle && (
                <p className="mt-2 text-sm leading-6 text-muted-foreground">{inviteSubtitle}</p>
              )}
            </div>

            <div className="px-6 py-5 sm:px-7">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground">Уровень доступа</p>
                    <p className="mt-1 text-sm text-muted-foreground">{inviteSecondaryLine}</p>
                  </div>
                </div>

                {hasManagementTargetIdentity && (
                  <div className="flex items-start gap-3 border-t border-border/70 pt-4">
                    <UserPlus className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground">Получатель</p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {managementInviteData.target_first_name} {managementInviteData.target_last_name}
                        {' · '}{managementInviteData.target_position}
                      </p>
                    </div>
                  </div>
                )}

                {managementInviteData.description && (
                  <div className="flex items-start gap-3 border-t border-border/70 pt-4">
                    <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground">Описание</p>
                      <p className="mt-1 text-sm text-muted-foreground">{managementInviteData.description}</p>
                    </div>
                  </div>
                )}

                <div className={cn(
                  'flex items-start gap-3',
                  (managementInviteData.description || hasManagementTargetIdentity) && 'border-t border-border/70 pt-4'
                )}>
                  <CalendarDays className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground">Принять приглашение до</p>
                    <p className="mt-1 text-sm text-muted-foreground">{validityDate}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Использовано {managementInviteData.current_uses} из {managementInviteData.max_uses}
                    </p>
                  </div>
                </div>
              </div>

              {shouldCollectName && (
                <div className="mt-5 space-y-2 border-t border-border/70 pt-4">
                  <p className="text-sm font-medium text-foreground">Укажите ваше имя и фамилию</p>
                  <p className="text-xs text-muted-foreground">Изменить их впоследствии сможет только ваш руководитель.</p>
                  <input
                    type="text"
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                    placeholder="Имя *"
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30"
                  />
                  <input
                    type="text"
                    value={lastName}
                    onChange={e => setLastName(e.target.value)}
                    placeholder="Фамилия *"
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
              )}

              {shouldCollectPosition && (
                <div className="mt-5 space-y-2 border-t border-border/70 pt-4">
                  <p className="text-sm font-medium text-foreground">Укажите вашу должность</p>
                  <p className="text-xs text-muted-foreground">Должность будет отображаться в списке сотрудников УК.</p>
                  <input
                    type="text"
                    value={position}
                    onChange={e => setPosition(e.target.value)}
                    placeholder="Должность *"
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
              )}

              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <Button
                  onClick={acceptManagementInvite}
                  disabled={isInviteActionDisabled}
                  className="w-full sm:flex-1"
                >
                  {accepting ? 'Принятие...' : 'Принять приглашение'}
                </Button>

                <Button
                  variant="outline"
                  onClick={declineInvite}
                  className="w-full sm:flex-1"
                >
                  Отклонить
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )
    }

    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="text-center">
          <XCircle className="w-12 h-12 text-error mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">Приглашение недействительно</h3>
          <p className="text-muted-foreground mb-4">
            Это приглашение не найдено или уже использовано
          </p>
          <Button onClick={() => navigate('/')}>
            Вернуться к студиям
          </Button>
        </Card>
      </div>
    )
  }

  if (!inviteData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="text-center">
          <XCircle className="w-12 h-12 text-error mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">Приглашение недействительно</h3>
          <p className="text-muted-foreground mb-4">
            Это приглашение не найдено или уже использовано
          </p>
          <Button onClick={() => navigate('/')}>
            Вернуться к студиям
          </Button>
        </Card>
      </div>
    )
  }

  // Рендеринг для Studio инвайтов
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-lg overflow-hidden border border-border/70 p-0 shadow-sm">
        <div className="border-b border-border/70 px-6 py-6 sm:px-7">
          <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <UserPlus className="h-6 w-6" />
          </div>
          <p className="mb-2 text-sm text-muted-foreground">{inviteTitle}</p>
          <h2 className="text-2xl font-semibold tracking-tight text-foreground">{invitePrimaryLine}</h2>
          {inviteSubtitle && (
            <p className="mt-2 text-sm leading-6 text-muted-foreground">{inviteSubtitle}</p>
          )}
        </div>

        <div className="px-6 py-5 sm:px-7">
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">Область доступа</p>
                <p className="mt-1 text-sm text-muted-foreground">{studioAccessSummary}</p>
              </div>
            </div>

            {hasStaffTargetIdentity && (
              <div className="flex items-start gap-3 border-t border-border/70 pt-4">
                <UserPlus className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground">Получатель</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {[inviteData.target_first_name, inviteData.target_last_name].filter(Boolean).join(' ')}
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-start gap-3 border-t border-border/70 pt-4">
              <CalendarDays className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">Принять приглашение до</p>
                <p className="mt-1 text-sm text-muted-foreground">{validityDate}</p>
              </div>
            </div>
          </div>

          {shouldCollectName && (
            <div className="mt-5 space-y-2 border-t border-border/70 pt-4">
              <p className="text-sm font-medium text-foreground">Укажите ваше имя и фамилию</p>
              <p className="text-xs text-muted-foreground">Изменить их впоследствии сможет только ваш руководитель.</p>
              <input
                type="text"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                placeholder="Имя *"
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30"
              />
              <input
                type="text"
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                placeholder="Фамилия *"
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
          )}

          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <Button
              onClick={acceptInvite}
              disabled={isInviteActionDisabled}
              className="w-full sm:flex-1"
            >
              {accepting ? 'Принятие...' : 'Принять приглашение'}
            </Button>

            <Button
              variant="outline"
              onClick={declineInvite}
              className="w-full sm:flex-1"
            >
              Отклонить
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
}
